import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { u2000BuildAppRouteHref } from "../../../../../../u2000Core/tools/u2000OpenAppRouteInNewTab";
import U2000BasicDialog from "../../../../../../u2000Core/components/dialog/U2000BasicDialog";
import U2000DetailTable from "../../../../../../u2000Core/components/tables/detail/U2000DetailTable";
import { useU2000GridStateHandlers } from "../../../../../../u2000Core/reducer/formReducer/U2000GridStateHandlers";
import type { U2000TableColumn } from "../../../../../../u2000Core/components/tables/basic/U2000BasicTableList";
import { u3125FormatCurrency } from "../../../../../../u3125Facture/U3125";
import { NS } from "../../../../../U3121";
import { type U3121BillingTimesheetDetailLine, u3121BillingSummaryTimesheetOpenPath } from "../../../../../U3121Service";
import { useU3121BillingSummaryTimesheetDetailHandlers } from "../../../../../U3121UseBillingSummaryHandlers";
import U3121BillingSummaryDetailDlgBody from "./U3121BillingSummaryDetailDlgBody";
import { u3121InitBillingSummaryDetailGrid } from "./u3121InitBillingSummaryDetailGrid";

export interface U3121BillingSummaryTimesheetDetailDlgProps {
  isOpen: boolean;
  onClose: () => void;
  projectId: string;
}

function formatDate(value: unknown): string {
  if (value == null || value === "") return "";
  const s = String(value);
  return s.length >= 10 ? s.slice(0, 10) : s;
}

function formatEmployee(line: U3121BillingTimesheetDetailLine): string {
  const emp = line.employee;
  const first = (emp?.firstName ?? line.employeeFirstName)?.trim() ?? "";
  const last = (emp?.lastName ?? line.employeeLastName)?.trim() ?? "";
  const full = `${first} ${last}`.trim();
  if (full) return full;
  return (emp?.codeEmployee ?? line.employeeCode)?.trim() ?? "";
}

function formatPeriod(line: U3121BillingTimesheetDetailLine): string {
  return formatDate(line.periodDate ?? line.periodStart);
}

function getColumnsDef(t: TFunction): U2000TableColumn[] {
  return [
    {
      name: "employee",
      label: t("colBillingSummaryEmployee", { ns: NS }),
      inGridField: false,
      sortable: true,
      isDefaultColumn: true,
      isDefaultSortedColumn: true,
      widthClass: "w-[18%]",
      type: "string",
      align: "left",
      display: (_c, _v, row) => formatEmployee(row as U3121BillingTimesheetDetailLine),
    },
    {
      name: "period",
      label: t("colBillingSummaryPeriod", { ns: NS }),
      inGridField: false,
      sortable: false,
      isDefaultColumn: true,
      widthClass: "w-[18%]",
      type: "string",
      align: "left",
      display: (_c, _v, row) => formatPeriod(row as U3121BillingTimesheetDetailLine),
    },
    {
      name: "status",
      label: t("colBillingSummaryTimesheetStatus", { ns: NS }),
      inGridField: true,
      sortable: true,
      isDefaultColumn: true,
      widthClass: "w-[14%]",
      type: "string",
      align: "left",
    },
    {
      name: "reportedHours",
      label: t("lblTotalReportedHours", { ns: NS }),
      inGridField: true,
      sortable: true,
      isDefaultColumn: true,
      widthClass: "w-[10%]",
      type: "number",
      align: "right",
    },
    {
      name: "expenseAmount",
      label: t("lblTotalExpenseAmount", { ns: NS }),
      inGridField: true,
      sortable: true,
      isDefaultColumn: true,
      widthClass: "w-[12%]",
      type: "number",
      align: "right",
      display: (_c, value) => u3125FormatCurrency(value),
    },
    {
      name: "equipmentAmount",
      label: t("lblTotalEquipmentAmount", { ns: NS }),
      inGridField: true,
      sortable: true,
      isDefaultColumn: true,
      widthClass: "w-[12%]",
      type: "number",
      align: "right",
      display: (_c, value) => u3125FormatCurrency(value),
    },
    {
      name: "link",
      label: t("colBillingSummaryOpen", { ns: NS }),
      inGridField: false,
      sortable: false,
      isDefaultColumn: true,
      widthClass: "w-[10%]",
      type: "string",
      align: "left",
      display: (_c, _v, row) => {
        const line = row as U3121BillingTimesheetDetailLine;
        const path = u3121BillingSummaryTimesheetOpenPath(line);
        if (!path) return null;
        return (
          <a
            href={u2000BuildAppRouteHref(path)}
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary-600 hover:underline"
            onClick={(e) => e.stopPropagation()}
          >
            {t("btnOpen", { ns: NS })}
          </a>
        );
      },
    },
  ];
}

const U3121BillingSummaryTimesheetDetailDlg: React.FC<U3121BillingSummaryTimesheetDetailDlgProps> = ({ isOpen, onClose, projectId }) => {
  const { t, i18n } = useTranslation();
  const gridStateHandlers = useU2000GridStateHandlers();
  const [loading, setLoading] = useState(false);
  const detailHandlers = useU3121BillingSummaryTimesheetDetailHandlers();

  useEffect(() => {
    if (!isOpen || !projectId.trim()) return;
    let cancelled = false;
    setLoading(true);
    void (async () => {
      const list = await detailHandlers.fetch(projectId);
      if (cancelled) return;
      const columns = getColumnsDef(t);
      u3121InitBillingSummaryDetailGrid(gridStateHandlers, columns, list);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, projectId, detailHandlers, t, i18n.language]);

  return (
    <U2000BasicDialog isOpen={isOpen} onClose={() => onClose()} title={t("dlgBillingSummaryTimesheetsTitle", { ns: NS })} width="x-large" height="max-h-[90vh]">
      <U3121BillingSummaryDetailDlgBody onClose={onClose}>
        {loading ? (
          <p className="text-sm text-gray-600">{t("msgLoading", { ns: NS })}</p>
        ) : gridStateHandlers.getTotalRows() === 0 ? (
          <p className="text-sm text-gray-600">{t("msgBillingSummaryDetailEmpty", { ns: NS })}</p>
        ) : (
          <div className="min-h-0 flex-1 overflow-auto">
            <U2000DetailTable
              gridStateHandlers={gridStateHandlers}
              allowRowSelection={false}
              label={t("lblBillingSummaryDetailCount", { count: gridStateHandlers.getTotalRows(), ns: NS })}
            />
          </div>
        )}
      </U3121BillingSummaryDetailDlgBody>
    </U2000BasicDialog>
  );
};

export default U3121BillingSummaryTimesheetDetailDlg;
